/* eslint-disable no-shadow */
/* eslint-disable no-console */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Device, Call } from '@twilio/voice-sdk'; // ✅ novo SDK tipado
import {
  FaPhoneAlt,
  FaPhoneSlash,
  FaMicrophone,
  FaMicrophoneSlash,
} from 'react-icons/fa';
import { FiRefreshCw } from 'react-icons/fi';
import SmallSpinner from 'components/SmallSpinner';
import { TwilioError } from '@twilio/voice-sdk/es5/twilio/errors';
import { toast } from 'react-toastify';
import { colors } from 'styles/theme/colors';

import { getToken } from '../../services/phoneCall';

import {
  Container,
  PhoneNumber,
  Buttons,
  TurnOnTurnOff,
  Mic,
  CallStatus,
} from './styles';

interface PhoneCallProps {
  phoneNumber: string | undefined;
  appointmentId: string;
  inCall: boolean;
  setInCall: React.Dispatch<React.SetStateAction<boolean>>;
  onHangUp: () => void;
}

export type StatusType =
  | 'init'
  | 'ready'
  | 'calling'
  | 'inProgress'
  | 'closed'
  | 'error'
  | 'serverError';

type StatusMessageType = {
  [key in StatusType]: string;
};

const statusMessage: StatusMessageType = {
  init: 'Preparando para realizar a ligação',
  ready: 'Selecione o botão verde para chamar',
  calling: 'Chamando',
  inProgress: 'Ligação em andamento',
  closed: 'Ligação encerrada',
  error: 'Não foi possível realizar a ligação',
  serverError: 'Erro de conexão com o servidor',
};

const buttonMessageName: StatusMessageType = {
  calling: 'Chamando...',
  ready: 'Chamar',
  closed: 'Encerrado',
  error: 'Tentar novamente',
  init: '',
  inProgress: 'Em andamento',
  serverError: 'Tentar novamente',
};

const PhoneCall: React.FC<PhoneCallProps> = ({
  phoneNumber,
  appointmentId,
  inCall,
  setInCall,
  onHangUp,
}) => {
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [status, setStatus] = useState<StatusType>('init');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const callRef = useRef<Call | null>(null);

  const fetchToken = useCallback(async () => {
    try {
      const token = await getToken();
      tokenRef.current = token;
      setStatus('ready');
      setIsLoading(false);
      return token;
    } catch (error) {
      console.error(error);
      setStatus('serverError');
      setInCall(false);
      return null;
    }
  }, [setInCall]);

  // 🔹 Inicialização do Device
  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  const setupDevice = useCallback(
    (token: string): Device => {
      const device = new Device(token, {
        logLevel: 'info',
        enableImprovedSignalingErrorPrecision: true,
      });

      device.on('error', (error: TwilioError) => {
        setStatus('error');
        console.error(error);
        const errorMessage =
          error.description || error.explanation || error.name || error.message;
        toast.error(`Erro no dispositivo: ${errorMessage} - ${error.code}`);
        setError(`Erro no dispositivo: ${error.code}`);
      });

      device.on('incoming', (call: Call) => {
        // Recebendo chamada (caso necessário)
        setStatus('inProgress');
        callRef.current = call;
        call.on('disconnect', () => {
          setInCall(false);
          setStatus('closed');
          onHangUp();
        });
      });

      device.register();
      return device;
    },
    [onHangUp, setInCall],
  );

  const startCall = async (token: string) => {
    const device = setupDevice(token);
    const tokenLocal = localStorage.getItem('@Cinco:token');

    const call = await device.connect({
      params: {
        toNumber: phoneNumber || '',
        token: tokenLocal || '',
        appointmentId: appointmentId || '',
      },
    });
    callRef.current = call;
    setInCall(true);
    setStatus('calling');

    call.on('error', (error: TwilioError) => {
      setStatus('error');
      console.error(error);
      const errorMessage =
        error.description || error.explanation || error.name || error.message;
      toast.error(`Erro na chamada: ${errorMessage} - ${error.code}`);
      setError(`Erro na chamada: ${error.code}`);
    });

    call.on('disconnect', () => {
      setInCall(false);
      setStatus('closed');
      onHangUp();
    });
  };

  // 🔹 Iniciar ou encerrar chamada
  const handleCall = async () => {
    if (inCall) {
      callRef.current?.disconnect();
      setInCall(false);
      onHangUp();
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      let token = tokenRef.current;
      if (!token) {
        token = await fetchToken();
      }
      if (!token) return;
      await startCall(token);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Mutar/desmutar
  const handleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const newMuteState = !call.isMuted();
    call.mute(newMuteState);
    setIsMuted(newMuteState);
  }, []);

  const handleFunctionOption =
    status === 'serverError' || status === 'error' || error;

  return (
    <Container>
      <strong>Número:</strong>
      <PhoneNumber>
        <span>{phoneNumber}</span>
      </PhoneNumber>
      <Buttons>
        <TurnOnTurnOff
          onClick={handleCall}
          inCall={inCall}
          disabled={isLoading}
        >
          {inCall ? (
            <>
              <FaPhoneSlash />
              <span>Desligar</span>
            </>
          ) : status === 'init' ? (
            <SmallSpinner />
          ) : (
            <>
              {!handleFunctionOption ? <FaPhoneAlt /> : <FiRefreshCw />}
              <span>{buttonMessageName[status]}</span>
            </>
          )}
        </TurnOnTurnOff>
        <Mic onClick={handleMute}>
          {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </Mic>
      </Buttons>
      <CallStatus callStatus={status}>
        <strong>STATUS:</strong>
        <span>{statusMessage[status]}</span>
      </CallStatus>
      {!!error && (
        <div style={{ marginTop: 16, color: colors.danger }}>{error}</div>
      )}
    </Container>
  );
};

export default PhoneCall;
